import axios from 'axios'
import store from '@/store'

const state = {
  reservations: [],
  codes: [],
  currentReservation: null,
  currentCode: null,
  loading: false,
  error: null,
  filters: {
    role: null,
    status: null,
    dateRange: null
  }
}

const getters = {
  getReservations: (state) => state.reservations,
  getCodes: (state) => state.codes,
  getCurrentReservation: (state) => state.currentReservation,
  getCurrentCode: (state) => state.currentCode,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,

  getReservationById: (state) => (id) => {
    return state.reservations.find(r => r.id === id)
  },

  getCodesByReservation: (state) => (reservationId) => {
    return state.codes.filter(c => c.reservation_id === reservationId)
  },

  getCodesByStatus: (state) => (status) => {
    return state.codes.filter(c => c.qr_status === status)
  },

  getActiveReservations: (state) => {
    return state.reservations.filter(r => r.status === 'active')
  },

  getOwnerReservations: (state) => {
    return state.reservations.filter(r => r.role === 'owner')
  },

  getBeneficiaryReservations: (state) => {
    return state.reservations.filter(r => r.role === 'beneficiary')
  }
}

const mutations = {
  SET_RESERVATIONS(state, reservations) {
    state.reservations = reservations
  },

  SET_CODES(state, codes) {
    state.codes = codes
  },

  ADD_RESERVATION(state, reservation) {
    state.reservations.push(reservation)
  },

  ADD_CODE(state, code) {
    state.codes.push(code)
  },

  UPDATE_RESERVATION(state, updatedReservation) {
    const index = state.reservations.findIndex(r => r.id === updatedReservation.id)
    if (index !== -1) {
      state.reservations.splice(index, 1, updatedReservation)
    }
  },

  UPDATE_CODE(state, updatedCode) {
    const index = state.codes.findIndex(c => c.id === updatedCode.id)
    if (index !== -1) {
      state.codes.splice(index, 1, updatedCode)
    }
  },

  UPDATE_CODE_STATUS(state, { codeId, status }) {
    const code = state.codes.find(c => c.id === codeId)
    if (code) {
      code.qr_status = status
    }
  },

  SET_CURRENT_RESERVATION(state, reservation) {
    state.currentReservation = reservation
  },

  SET_CURRENT_CODE(state, code) {
    state.currentCode = code
  },

  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  CLEAR_ERROR(state) {
    state.error = null
  },

  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },

  REMOVE_RESERVATION(state, reservationId) {
    state.reservations = state.reservations.filter(r => r.id !== reservationId)
  },

  REMOVE_CODE(state, codeId) {
    state.codes = state.codes.filter(c => c.id !== codeId)
  }
}

const actions = {
  // ============= RESERVATIONS =============

  async FETCH_RESERVATIONS({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'get',
        url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/list/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        params
      }
      
      const response = await axios(config)
      const reservations = Array.isArray(response.data) ? response.data : []
      
      // Agregar propiedad 'role' basada en el contexto
      const enrichedReservations = reservations.map(r => ({
        ...r,
        role: r.owner_company === store.getters.getProfile.id_company ? 'owner' : 'beneficiary'
      }))
      
      commit('SET_RESERVATIONS', enrichedReservations)
      return enrichedReservations
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al obtener reservas'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async CREATE_RESERVATION({ commit }, data) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'post',
        url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/create/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data
      }
      
      const response = await axios(config)
      const reservation = { ...response.data, role: 'owner' }
      
      commit('ADD_RESERVATION', reservation)
      return reservation
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al crear reserva'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async UPDATE_RESERVATION({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'patch',
        url: `https://empresa-service.intrare.app/Parking/invitations/reservations/${id}/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data
      }
      
      const response = await axios(config)
      commit('UPDATE_RESERVATION', response.data)
      return response.data
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al actualizar reserva'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async CANCEL_RESERVATION({ commit }, reservationId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'patch',
        url: `https://empresa-service.intrare.app/Parking/invitations/reservations/${reservationId}/cancel/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      
      const response = await axios(config)
      commit('UPDATE_RESERVATION', response.data)
      return response.data
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al cancelar reserva'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ============= CODES =============

  async FETCH_CODES({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'get',
        url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/codes/list/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        params
      }
      
      const response = await axios(config)
      const codes = Array.isArray(response.data) ? response.data : []
      
      commit('SET_CODES', codes)
      return codes
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al obtener códigos'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async CREATE_CODE({ commit }, data) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'post',
        url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/codes/create/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data
      }
      
      const response = await axios(config)
      commit('ADD_CODE', response.data)
      return response.data
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al crear código'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async UPDATE_CODE_STATUS({ commit }, { id, action }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'patch',
        url: `https://empresa-service.intrare.app/Parking/invitations/reservations/codes/${id}/status/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: { action }
      }
      
      const response = await axios(config)
      commit('UPDATE_CODE', response.data)
      return response.data
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al actualizar estado'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async DELETE_CODE({ commit }, { id, reason = '', notify_provider = true }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'delete',
        url: `https://empresa-service.intrare.app/Parking/invitations/reservations/codes/${id}/delete/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: { 
          reason: reason || '',
          notify_provider 
        }
      }
      
      await axios(config)
      // El servidor devuelve 204 No Content, así que removemos el código del estado local
      commit('REMOVE_CODE', id)
      return true
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.detail) || 
                       (error.response && error.response.data && error.response.data.error) || 
                       'Error al cancelar código'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async RESEND_CODE({ commit }, { id, email, phone }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const config = {
        method: 'patch',
        url: `https://empresa-service.intrare.app/Parking/invitations/reservations/codes/${id}/resend/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: { email, phone }
      }
      
      const response = await axios(config)
      commit('UPDATE_CODE', response.data)
      return response.data
    } catch (error) {
      const errorMsg = (error.response && error.response.data && error.response.data.error) || 'Error al reenviar código'
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ============= UTILITIES =============

  SET_FILTERS({ commit }, filters) {
    commit('SET_FILTERS', filters)
  },

  CLEAR_FILTERS({ commit }) {
    commit('SET_FILTERS', { role: null, status: null, dateRange: null })
  },

  CLEAR_ERROR({ commit }) {
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
